import {inject, observer} from "mobx-react";
import * as React from "react";
import * as url from "url";

import {CommonStore, DashboardStore, FilterStore, SnapshotStore, TrustedTokenStore} from "../../stores";
import * as Sentry from "@sentry/react";
import {Alert} from "react-bootstrap";
import {
    CustomView,
    DeviceType,
    TableauEventType,
    TableauViz,
    Toolbar,
    Workbook,
    CrosstabFileFormat,
    TableauDialogType,
    FilterUpdateType,
} from "@tableau/embedding-api";

const HAS_EXTENSION_MESSAGE = "hasExtension";
const GET_CONTENT_MESSAGE = "getContent";
const RECEIVE_CONTENT_MESSAGE = "sentContent";

interface IDashboardEvent {
    type: string,
    dashboard: string
}
interface ITableauDisplayProps {
    dashboardStore?: DashboardStore
    trustedTokenStore?: TrustedTokenStore
    filterStore?: FilterStore
    snapshotStore?: SnapshotStore
    commonStore?: CommonStore
    onRef: (ref) => void
    dashboardId: number
    viewId?: string | null
    refreshView?: string | null
    vizOptions: any
    queryParameters: any
    handleLoaded: (name) => void
    handleTabSwitch: (name) => void
    shouldSaveView?: boolean
    getSnapshot?: (snapshot: string | Blob) => void
    token?: string
    jwtToken?: string
    style?: any
    match?: any
    overrideSheet?: string
    isResponsive?: boolean
}

interface ITableauDisplayState {
    alteredWorkbook?: Workbook
    didInvalidateToken?: boolean
    filterWorksheet?: any
    dashboardId?: number
    viewId?: string | null
    refreshView?: string | null
    revertAllAsync: boolean
    newDashboardRefresh: boolean
    vizLoaded: boolean
    vizNotLoadedWithError: boolean
    viz?: TableauViz
}
@inject("dashboardStore", "filterStore", "trustedTokenStore", "snapshotStore", "commonStore")
@observer
export class TableauDisplay extends React.Component<ITableauDisplayProps, ITableauDisplayState> {

    constructor(props: any) {
        super(props);
        this.state = {
            alteredWorkbook: undefined,
            didInvalidateToken: false,
            filterWorksheet: undefined,
            dashboardId: this.props.dashboardId,
            viewId: this.props.viewId,
            refreshView: this.props.refreshView,
            revertAllAsync: false,
            newDashboardRefresh: true,
            vizLoaded: false,
            vizNotLoadedWithError: false,
            viz: undefined
        };
    }

    container: HTMLDivElement | null;
    currentId?: string;
    currentViewId?: string | null;
    currentRefreshView?: string | null;
    lastEvent?: IDashboardEvent;

    componentDidMount(){
        window.addEventListener("message", this.handleExtensionMessages);
        this.props.onRef(this);
        this.fetchAndInit();
        this.props.snapshotStore!.updateSnapshotTrigger(this.getSnapshotUrl);
    }

    componentWillUnmount() {
        this.props.snapshotStore!.updateSnapshotTrigger(() => {});
        this.props.snapshotStore!.resetSnapshot();
        this.props.snapshotStore!.updateExtensionPresent(false);
        // remove event listeners
        if (this.state.viz) {
            this.state.viz.removeEventListener(TableauEventType.FilterChanged, () => {});
            this.state.viz.removeEventListener(TableauEventType.MarkSelectionChanged, () => {});
            this.state.viz.removeEventListener(TableauEventType.ParameterChanged, () => {});
            this.state.viz.removeEventListener(TableauEventType.TabSwitched, () => {});
            this.state.viz.removeEventListener(TableauEventType.FirstInteractive, () => {});
            this.state.viz.remove();
            this.setState({viz: undefined});
        }
    }

    static getDerivedStateFromProps(nextProps, state) {
        if (!state || nextProps.dashboardId !== state.dashboardId ) {
            return {alteredWorkbook: undefined, dashboardId: nextProps.dashboardId, viewId: nextProps.viewId, refreshView: nextProps.refreshView, newDashboardRefresh: true, revertAllAsync: false};
        }
        if (nextProps.viewId !== state.viewId) {
            if (nextProps.viewId) {
                return {alteredWorkbook: undefined, dashboardId: nextProps.dashboardId, viewId: nextProps.viewId, refreshView: nextProps.refreshView, newDashboardRefresh: false, revertAllAsync: false};
            }
            return {alteredWorkbook: undefined, dashboardId: nextProps.dashboardId, viewId: nextProps.viewId, refreshView: nextProps.refreshView, newDashboardRefresh: true, revertAllAsync: false};

        }
        if (nextProps.refreshView !== state.refreshView) {
            return {alteredWorkbook: undefined, dashboardId: nextProps.dashboardId, viewId: nextProps.viewId, refreshView: nextProps.refreshView, newDashboardRefresh: true, revertAllAsync: true};
        }
        return null;
    }

    componentDidUpdate(prevProps: Readonly<ITableauDisplayProps>, prevState: Readonly<ITableauDisplayState>, snapshot?: any) {
        if (prevState.dashboardId !== this.state.dashboardId || prevState.viewId !== this.state.viewId || prevState.refreshView !== this.state.refreshView) {
            this.fetchAndInit();
        }
    }

    handleExtensionMessages = (ev: MessageEvent) => {
        if(ev.data.message === RECEIVE_CONTENT_MESSAGE){
            const snapshotBlob: Blob = ev.data.blob;
            this.props.snapshotStore!.updateSnapshotBlob(snapshotBlob);
        } else if (ev.data === HAS_EXTENSION_MESSAGE){
            this.props.snapshotStore!.updateExtensionPresent(true);
        }
    };

    tableauEscapeString = (value: string) => {
        if (!value) return "";
        return value.replace(/[!*'();:@&=+$,/?#[\]]/g, "\\$&");
    };

    getQueryFiltersObject = (tableauEscape = false) => {
        if(!this.props.dashboardStore || !this.props.dashboardStore.dashboard) return {};
        const { query: parsedQuery} = url.parse(this.props.dashboardStore.dashboard.url, true);
        const params = JSON.parse(JSON.stringify(this.props.queryParameters));

        if (tableauEscape) {
            for (const param in params) {
                let value = params[param];
                if (!value) continue;
                if (value instanceof Array) {
                    for (let i = 0; i < value.length; i++) {
                        value[i] = this.tableauEscapeString(value[i]);
                    }
                    value = value.reduce(
                        (all, current) => `${all},${current}`
                    );
                } else {
                    value = this.tableauEscapeString(value);
                }
                params[param] = value;
            }
        }

        return Object.assign({}, parsedQuery, params);
    };

    getUrl = () => {
        if(!this.props.dashboardStore || !this.props.dashboardStore.dashboard) return;
        const vizUrl = this.state.viewId ? this.props.commonStore?.dashboardView?.url : this.props.dashboardStore.dashboard.url;
        const parsed = url.parse(vizUrl || "", true);
        const queryObj = this.getQueryFiltersObject(true);
        const stringified = Object.keys(queryObj).map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryObj[key])}`).join("&");
        const query = `?:embed=yes&:comments=no&:toolbar=yes&:refresh=yes${stringified ? "&" + stringified : ""}`;

        return parsed.protocol + "//" + parsed.host + parsed.pathname + query;
    };

    getBaseUrl = () => {
        if(!this.props.dashboardStore || !this.props.dashboardStore.dashboard) return;
        return this.state.viewId ? this.props.commonStore?.dashboardView?.url : this.props.dashboardStore.dashboard.url;
    };

    handleVizLoaded = async () => {
        if(this.state.viz) {
            this.props.handleLoaded(this.state.viz.workbook.activeSheet.name);
            this.props.filterStore!.viz3 = this.state.viz;
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const filterWorksheet = this.state.viz.workbook.activeSheet.worksheets.filter((ws: any) => ws.name === "ui_filters")[0];
            this.setState({ filterWorksheet: filterWorksheet, vizLoaded: true });

            if (this.props.queryParameters && Object.keys(this.props.queryParameters).length > 0) {
                setTimeout(async () => {
                    try {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        const worksheets = this.state.viz.workbook.activeSheet.worksheets || [this.state.viz.workbook.activeSheet];
                        for (const [paramName, paramValue] of Object.entries(this.props.queryParameters)) {
                            if (paramValue) {
                                const fieldName = paramName.replace(/_/g, " ").replace(/\b\w/g, char => char.toUpperCase());
                                for (const worksheet of worksheets) {
                                    try {
                                        await worksheet.applyFilterAsync(fieldName, String(paramValue), FilterUpdateType.Replace);
                                    } catch (e) {}
                                }
                            }
                        }
                    } catch (e) {}
                }, 1000);
            }
        }
    };

    /**
     * Initialize the viz via the Tableau JS API.
     * @return {void}
     */
    initTableau = async () => {
        if (
            !this.props.dashboardStore
            || !this.props.dashboardStore.dashboard
            || (
                this.currentId == this.props.dashboardStore.dashboard.workbook_id
                && this.state.viewId === this.currentViewId
                && this.state.refreshView === this.currentRefreshView)) return;
        this.currentId = this.props.dashboardStore.dashboard.workbook_id;
        this.currentViewId = this.state.viewId;
        this.currentRefreshView = this.state.refreshView;
        this.props.filterStore!.resetStore();
        let jwt: string | undefined = "";

        if(this.props.trustedTokenStore){
            jwt = await this.props.trustedTokenStore.getConnectedAppJwt();
        }

        const vizUrl = this.getUrl();
        const viz = new TableauViz();

        viz.src = vizUrl || null;
        viz.device = DeviceType.Desktop;
        viz.toolbar = Toolbar.Hidden;
        viz.hideTabs = true;
        viz.token = jwt;
       // viz.token = "..";
        // clean up
        if(this.state.viz && this.state.newDashboardRefresh) {
            this.state.viz.remove();
        }
        // listener for viz events
        const vizListener = (msg: Event) => {
            this.registerDashboardEvent({ type: msg.type, dashboard: viz.workbook.name || "" });

            const eventName = msg.type;
            if(eventName === "parameterchanged" || eventName === "tabswitched") {
                this.setState({ alteredWorkbook: viz.workbook || undefined });
                if (eventName === "tabswitched") {
                    this.props.handleTabSwitch(viz.workbook.activeSheet.name);
                }
            } else {
                this.setState({ alteredWorkbook: viz.workbook });
            }
        };

        if (this.state.vizLoaded && this.state.viz && !this.state.newDashboardRefresh) {
            if (this.state.revertAllAsync) {
                await this.state.viz.workbook.revertAllAsync();
                return;
            }
            const workbook = this.state.viz.workbook;
            const customViews = await workbook.getCustomViewsAsync();
            const baseUrl = this.getBaseUrl();
            const workbookName = workbook.name;
            if (customViews.length > 0 && baseUrl) {
                const viewNameBase = baseUrl.substring(baseUrl.lastIndexOf("_CustomView_"), baseUrl.length);
                const customView = customViews.find((view: CustomView) => view.name === workbookName + viewNameBase);
                const customViewName = customView ? customView.name : undefined;
                if (customViewName) {
                    await this.state.viz.workbook.showCustomViewAsync(customViewName);
                    return;
                }
            }
            this.state.viz.remove();
        }

        if (this.container && this.props.isResponsive) {
            viz.width = this.container.offsetWidth.toString();
            viz.height = this.container.offsetHeight.toString();
        } else if (this.container) {
            viz.height = "768px";
            viz.width = "1366px";
        }

        viz.addEventListener(TableauEventType.FirstInteractive, async () => await this.handleVizLoaded());
        viz.addEventListener(TableauEventType.FilterChanged, vizListener);
        viz.addEventListener(TableauEventType.MarkSelectionChanged, vizListener);
        viz.addEventListener(TableauEventType.ParameterChanged, vizListener);
        viz.addEventListener(TableauEventType.TabSwitched, vizListener);
        // add custom parameter
        const queryObj = this.getQueryFiltersObject(true);
        Object.keys(queryObj).forEach((key) => {
            const customParameter = document.createElement("custom-parameter");
            customParameter.setAttribute("name", key);
            customParameter.setAttribute("value", queryObj[key] || "");
            viz.appendChild(customParameter);
        });

        document.getElementById("vizContainer")?.appendChild(viz);
        this.setState({ viz: viz }, () => {
            setTimeout(() => {
                if (!this.state.vizLoaded) {
                    this.setState({ vizNotLoadedWithError: true });
                }
            }, 40000);
        });
    };

    registerDashboardEvent = (event: IDashboardEvent): void => {
        // account for several events being fired for one filter selection
        if(this.lastEvent && this.lastEvent.type === event.type && this.lastEvent.dashboard === event.dashboard) return;
        this.lastEvent = event;
        setTimeout(() => this.lastEvent = undefined, 200);
    };

    getAlteredWorkbook = () => {
        return this.state.alteredWorkbook;
    };

    getAlteredWorkbookName = () => {
        const wb = this.getAlteredWorkbook();

        if(wb){
            return wb.name;
        } else {
            return undefined;
        }
    };

    handleExport = async (sheetName: string, fileType: CrosstabFileFormat) => {
        if (!this.state.viz) {
            console.error("Unable to export crosstab: no viz available");
            Sentry.addBreadcrumb({
                message: "Unable to export crosstab: no viz available",
                data: {},
            });
        }
        const tableauViz = this.state.viz;

        if (!tableauViz) {
            return;
        }
        if (fileType === CrosstabFileFormat.CSV) {
            await tableauViz.exportCrosstabAsync(sheetName, CrosstabFileFormat.CSV);
        } else {
            await tableauViz.exportCrosstabAsync(sheetName, CrosstabFileFormat.Excel);
        }
    };

    handleExportPDF = async () => {
        if (!this.state.viz) {
            console.error("Unable to export crosstab: no viz available");
            Sentry.addBreadcrumb({
                message: "Unable to export crosstab: no viz available",
                data: {},
            });
        }
        const tableauViz = this.state.viz;

        if (!tableauViz) {
            return;
        }
        await tableauViz.displayDialogAsync(TableauDialogType.ExportPDF);
    };

    handleExportDashboardPNG = async () => {
        if (!this.state.viz) {
            console.error("Unable to export dashboard: no viz available");
            Sentry.addBreadcrumb({
                message: "Unable to export dashboard: no viz available",
                data: {},
            });
        }
        const tableauViz = this.state.viz;
        if (!tableauViz) {
            return;
        }
        await tableauViz.exportImageAsync();
    };

    rememberAlteredWorkbookCustomView = async (defaultWb?: Workbook | undefined): Promise<string | undefined> => {
        let wb = this.getAlteredWorkbook();
        let snapshotUrl: string | undefined = undefined;

        if (!wb && defaultWb) {
            wb = defaultWb;
        }
        if(wb){
            const workbook = wb;
            const now = Date.now();
            const name = this.getAlteredWorkbookName();

            await workbook.saveCustomViewAsync(name + "_CustomView_" + now);

            snapshotUrl = workbook.activeCustomView?.url;
        }

        return snapshotUrl;
    };

            private isWorkbookReady = (): boolean => {
        try {
            return !!(this.state.vizLoaded && this.state.viz && this.state.viz.workbook);
        } catch (error) {
            return false;
        }
    };

    getSnapshotUrl = async () => {
        if(this.props.snapshotStore!.extensionPresent){
            this.postMessageToIFrame(GET_CONTENT_MESSAGE);
        }

        if (!this.isWorkbookReady()) {
            console.warn("Viz or workbook not ready yet, cannot get snapshot URL");
            return;
        }

        try {
            const workbook = this.state.viz!.workbook;

            if (!workbook) {
                console.warn("Workbook not available yet");
                return;
            }

            if(this.state.alteredWorkbook) {
                this.props.snapshotStore!.updateSnapshotUrl(await this.rememberAlteredWorkbookCustomView() as string);
            } else {
                this.props.snapshotStore!.updateSnapshotUrl(await this.rememberAlteredWorkbookCustomView(workbook) as string);
            }
        } catch (error) {
            console.error("Error accessing workbook:", error);
            if(this.state.alteredWorkbook) {
                this.props.snapshotStore!.updateSnapshotUrl(await this.rememberAlteredWorkbookCustomView() as string);
            }
        }
    };

    async fetchAndInit() {
        if(!this.props.dashboardStore || !this.props.commonStore) return;
        await this.props.dashboardStore.getDashboardById(this.props.dashboardId, this.props.overrideSheet);
        if (this.props.viewId) {
            await this.props.commonStore.fetchDashboardViewById(this.props.viewId);
        }
        await this.props.dashboardStore!.getDashboardExports();
        this.initTableau();
    }

    postMessageToIFrame = (msg) => {
        /*
        * There are currently four message types that our ui and extension use to communicate (two pairs of a request and a response):
        * CHECK_EXTENSION_MESSAGE (currently unused) and HAS_EXTENSION_MESSAGE are for determining if the current dashboard has the extension present
        * GET_CONTENT_MESSAGE and RECEIVE_CONTENT_MESSAGE are for fetching the snapshot blob of the dashboard using the extension
        *
        * CHECK_EXTENSION_MESSAGE ("checkExtension") is currently unused
        */
        this.container!.getElementsByTagName("iframe")[0].contentWindow!.postMessage(msg, "*");
    };

    handleRef = ref => this.container = ref;

    render() {
        if (!this.props.dashboardStore || !this.props.dashboardStore.dashboard) return null;

        if (this.state.vizNotLoadedWithError) {
            return (
                <div>
                    <Alert>The dashboard view is temporarily unavailable. If this issue persists please contact
                        Enflux support using the chat at the bottom right of your window or email to <a href="mailto:support@enflux.com" target="_blank" rel="noreferrer">support@enflux.com</a>.</Alert>
                </div>
            );
        }
        return (
            <div className={"tableauDisplay" + (this.state.filterWorksheet && (this.props.filterStore!.isFilterLoading || this.props.filterStore!.isDashboardLoading) ? " filter-loading" : "")}>
                <div className="iFrame" style={{margin: "auto", width: "100%"}}>
                    <div id="vizContainer" style={this.props.style} ref={this.handleRef} />
                </div>
            </div>
        );
    }

}
